import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";

import type { RootState } from "../../../store/store";
import { isAdminFromToken } from "../../../utils/authToken";

import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";

import {
  archiveEvaluationFormApi,
  EvaluationForm,
  EvaluationQuestion,
  getEvaluationFormByIdApi,
  listEvaluationFormsApi,
  updateEvaluationFormApi,
} from "../evaluationFormsApi";

import CreateEvaluationFormModal from "../../../components/modal/evaluation/CreateEvaluationFormModal";
import QuestionPanel, { isChoiceType } from "../components/QuestionPanel";
import FormInfoDialog, { ANSWERER_OPTIONS } from "../components/FormInfoDialog";

import "./EvaluationFormsPage.css";

type CategoryFilter = "ALL" | "LOJA" | "FREELANCER" | "NR1";

const CATEGORY_FILTERS: { value: CategoryFilter; label: string }[] = [
  { value: "ALL", label: "Todos" },
  { value: "LOJA", label: "Loja" },
  { value: "FREELANCER", label: "Freelancer" },
  { value: "NR1", label: "NR1" },
];

const CATEGORY_LABEL: Record<string, string> = { LOJA: "Loja", FREELANCER: "Freelancer", NR1: "NR1" };

/** Quantas perguntas aparecem antes do "Ver todas". */
const QUESTIONS_PREVIEW = 8;

const activeQuestions = (form: EvaluationForm) => (form.questions ?? []).filter((q) => !q.archivedAt);

const answeredByText = (form: EvaluationForm) => {
  if (form.category === "NR1") return "anônimo, por link";
  const labels = (form.allowedAnswererTypes ?? []).map(
    (t) => ANSWERER_OPTIONS.find((o) => o.value === t)?.label.toLowerCase() ?? t,
  );
  if (labels.length === 0) return "sem tipo de usuário definido";
  return `respondido por ${labels.length > 1 ? `${labels.slice(0, -1).join(", ")} ou ${labels[labels.length - 1]}` : labels[0]}`;
};

const questionTypeText = (q: EvaluationQuestion) => {
  const opts = (q.options ?? []).length;
  switch (q.type) {
    case "BOOLEAN":
      return "Sim ou Não";
    case "SCALE":
      return q.minValue != null && q.maxValue != null ? `Escala ${q.minValue} a ${q.maxValue}` : "Escala";
    case "TEXT":
      return "Texto livre";
    case "DATE":
      return "Data";
    case "IMAGE_UPLOAD":
      return "Foto";
    case "SINGLE_CHOICE":
      return `Escolha única · ${opts} ${opts === 1 ? "opção" : "opções"}`;
    case "MULTI_CHOICE":
      return `Múltipla escolha · ${opts} ${opts === 1 ? "opção" : "opções"}`;
    default:
      return String(q.type);
  }
};

const CategoryPill: React.FC<{ category: string }> = ({ category }) => (
  <span className={`fForms__cat fForms__cat--${category.toLowerCase()}`}>{CATEGORY_LABEL[category] ?? category}</span>
);

/**
 * Formulários de perguntas — lista à esquerda, formulário escolhido à direita.
 *
 * Substitui o hub de 2 cards (Cadastrar / Gerenciar). A gestão que o
 * `UpdateEvaluationFormModal` fazia (dados, perguntas, opções, arquivar)
 * veio para esta tela e para o `QuestionPanel`; o cadastro de formulário novo
 * continua no `CreateEvaluationFormModal`, que tem a cópia de perguntas de
 * outro formulário. "Nova versão" e reordenar arrastando, que aparecem no
 * design, não existem na API e ficaram de fora.
 */
const EvaluationFormsAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());

  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);

  const [forms, setForms] = useState<EvaluationForm[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<CategoryFilter>("ALL");
  const [showArchived, setShowArchived] = useState(false);

  const [selectedId, setSelectedId] = useState<string>("");
  const [detail, setDetail] = useState<EvaluationForm | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [showArchivedQuestions, setShowArchivedQuestions] = useState(false);
  /** Celular: a lista e o detalhe não cabem lado a lado. */
  const [mobileView, setMobileView] = useState<"list" | "detail">("list");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Painel de pergunta: `undefined` fechado, `null` pergunta nova.
  const [panelQuestion, setPanelQuestion] = useState<EvaluationQuestion | null | undefined>(undefined);

  const detailReq = useRef(0);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }

    if (!isAdmin) {
      toast.error("Acesso restrito: somente ADMIN.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, isAdmin, navigate]);

  const loadForms = useCallback(async () => {
    try {
      setLoadingList(true);
      const data = await listEvaluationFormsApi({ includeArchived: true, includeQuestions: true });
      setForms(data);
    } catch {
      toast.error("Erro ao listar formulários.");
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadDetail = useCallback(async (formId: string): Promise<EvaluationForm | null> => {
    if (!formId) {
      setDetail(null);
      return null;
    }
    const req = ++detailReq.current;
    try {
      setLoadingDetail(true);
      const data = await getEvaluationFormByIdApi(formId);
      if (req !== detailReq.current) return null;
      setDetail(data);
      return data;
    } catch {
      toast.error("Erro ao carregar formulário.");
      return null;
    } finally {
      if (req === detailReq.current) setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (token && isAdmin) loadForms();
  }, [token, isAdmin, loadForms]);

  const visibleForms = useMemo(() => {
    const term = search.trim().toLowerCase();
    return forms
      .filter((f) => (showArchived ? true : !f.archivedAt))
      .filter((f) => (category === "ALL" ? true : f.category === category))
      .filter((f) => (term ? (f.title ?? "").toLowerCase().includes(term) : true))
      .sort((a, b) => Number(!!a.archivedAt) - Number(!!b.archivedAt) || (a.title ?? "").localeCompare(b.title ?? "", "pt-BR"));
  }, [forms, search, category, showArchived]);

  // Desktop: abre o primeiro da lista; mantém o escolhido se ele continuar visível.
  useEffect(() => {
    if (loadingList) return;
    if (selectedId && visibleForms.some((f) => f.id === selectedId)) return;
    setSelectedId(visibleForms[0]?.id ?? "");
  }, [visibleForms, selectedId, loadingList]);

  useEffect(() => {
    setShowAllQuestions(false);
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const selectForm = (id: string) => {
    setSelectedId(id);
    setMobileView("detail");
  };

  /** Depois de mexer numa pergunta: recarrega o detalhe e a contagem da lista. */
  const refreshAfterQuestionChange = useCallback(
    async (questionId?: string) => {
      const fresh = await loadDetail(selectedId);
      loadForms();
      return fresh?.questions?.find((q) => q.id === questionId) ?? null;
    },
    [loadDetail, loadForms, selectedId],
  );

  const handleToggleArchive = async () => {
    if (!detail || archiving) return;
    try {
      setArchiving(true);
      if (!detail.archivedAt) {
        await archiveEvaluationFormApi(detail.id);
        toast.success("Formulário arquivado com sucesso.");
      } else {
        await updateEvaluationFormApi(detail.id, { archivedAt: null });
        toast.success("Formulário reativado com sucesso.");
      }
      setConfirmArchive(false);
      // Arquivado some da lista se os arquivados estão escondidos; mostra para não "perder" o formulário.
      if (!detail.archivedAt) setShowArchived(true);
      await Promise.all([loadForms(), loadDetail(detail.id)]);
    } catch {
      toast.error(!detail.archivedAt ? "Erro ao arquivar formulário." : "Erro ao reativar formulário.");
    } finally {
      setArchiving(false);
    }
  };

  const detailQuestions = useMemo(() => {
    const qs = (detail?.questions ?? []).filter((q) => (showArchivedQuestions ? true : !q.archivedAt));
    return qs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [detail, showArchivedQuestions]);

  const dimensionSuggestions = useMemo(() => {
    const set = new Set<string>();
    forms.forEach((f) => (f.questions ?? []).forEach((q) => q.dimension && set.add(q.dimension)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [forms]);

  if (!token || !isAdmin) return null;

  const isArchived = Boolean(detail?.archivedAt);
  const activeCount = detail ? activeQuestions(detail).length : 0;
  const scoringCount = detail ? activeQuestions(detail).filter((q) => q.type === "BOOLEAN").length : 0;
  const nextOrder = (detail?.questions ?? []).reduce((m, q) => Math.max(m, q.order ?? 0), 0) + 1;
  const shownQuestions = showAllQuestions ? detailQuestions : detailQuestions.slice(0, QUESTIONS_PREVIEW);
  const choiceWithoutOptions = (q: EvaluationQuestion) => isChoiceType(q.type) && (q.options ?? []).length === 0;

  const listCountLabel = `${visibleForms.length} ${visibleForms.length === 1 ? "formulário" : "formulários"}${
    showArchived ? "" : visibleForms.length === 1 ? " ativo" : " ativos"
  }`;

  return (
    <div className="fForms">
      <main className="fForms__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Formulários de perguntas" },
          ]}
          title="Formulários de perguntas"
          subtitle="Modelos de avaliação de loja, de freelancer e da NR1, com suas perguntas."
          onBack={goBack}
          backLabel="Voltar para Operações"
          actions={
            <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setIsCreateOpen(true)}>
              <EcoIcon name="plus" size={18} />
              Novo formulário
            </button>
          }
        />

        <div className={`fForms__layout fForms__layout--${mobileView}`}>
          {/* Lista */}
          <section className="ecoCard fForms__list" aria-label="Formulários">
            <div className="fForms__listTools">
              <label htmlFor="busca-form" className="ecoVisuallyHidden">
                Buscar formulário
              </label>
              <div className="ecoSearch">
                <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                <input
                  id="busca-form"
                  type="search"
                  className="ecoInput ecoInput--sm"
                  placeholder="Buscar por nome"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <EcoSegmented label="Categoria" options={CATEGORY_FILTERS} value={category} onChange={setCategory} className="fForms__catFilter" />
              <label className="qPanel__check fForms__archivedToggle">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                <span>Mostrar arquivados</span>
              </label>
              <span className="fForms__count" aria-live="polite">
                {loadingList ? "Carregando..." : listCountLabel}
              </span>
            </div>

            {!loadingList && visibleForms.length === 0 ? (
              <div className="fForms__pad">
                <p className="ecoEmpty">Nenhum formulário encontrado.</p>
              </div>
            ) : (
              <ul className="fForms__items">
                {visibleForms.map((f) => {
                  const n = activeQuestions(f).length;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        className={`fForms__item${f.id === selectedId ? " is-selected" : ""}`}
                        onClick={() => selectForm(f.id)}
                        aria-current={f.id === selectedId ? "true" : undefined}
                      >
                        <span className="fForms__itemTop">
                          <span className="fForms__itemTitle">{f.title}</span>
                          <CategoryPill category={f.category} />
                        </span>
                        <span className="fForms__itemMeta">
                          {f.archivedAt ? "Arquivado · " : ""}
                          {n} {n === 1 ? "pergunta" : "perguntas"} · {answeredByText(f)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Detalhe */}
          <section className="ecoCard fForms__detail" aria-label="Formulário selecionado" aria-busy={loadingDetail}>
            <button type="button" className="ecoLinkBtn fForms__backToList" onClick={() => setMobileView("list")}>
              <EcoIcon name="chevronLeft" size={16} />
              Todos os formulários
            </button>

            {!detail ? (
              <div className="fForms__pad">
                <p className="ecoEmpty">
                  {loadingDetail ? "Carregando formulário..." : "Escolha um formulário na lista."}
                </p>
              </div>
            ) : (
              <>
                <div className="fForms__detailHead">
                  <div className="fForms__detailInfo">
                    <div className="fForms__detailTitleRow">
                      <h2 className="fForms__detailTitle">{detail.title}</h2>
                      <CategoryPill category={detail.category} />
                      {isArchived ? <span className="ecoPill ecoPill--off">Arquivado</span> : null}
                    </div>
                    <span className="fForms__detailMeta">
                      {activeCount} {activeCount === 1 ? "pergunta" : "perguntas"}
                      {detail.category === "LOJA" ? `, ${scoringCount} ${scoringCount === 1 ? "entra" : "entram"} na nota` : ""} ·{" "}
                      {answeredByText(detail)} · criado em {new Date(detail.createdAt).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <div className="fForms__detailActions">
                    <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setIsInfoOpen(true)} disabled={isArchived}>
                      Editar dados
                    </button>
                    <button
                      type="button"
                      className={`ecoBtn ecoBtn--sm${isArchived ? "" : " ecoBtn--danger"}`}
                      onClick={() => setConfirmArchive(true)}
                    >
                      {isArchived ? "Reativar" : "Arquivar"}
                    </button>
                  </div>
                </div>

                <div className="fForms__qBar">
                  <span className="fForms__qHint">
                    {detail.category === "LOJA"
                      ? "Só perguntas do tipo Sim ou Não entram no cálculo da nota."
                      : "Perguntas na ordem em que aparecem para quem responde."}
                  </span>
                  <div className="fForms__qBarActions">
                    <label className="qPanel__check">
                      <input
                        type="checkbox"
                        checked={showArchivedQuestions}
                        onChange={(e) => setShowArchivedQuestions(e.target.checked)}
                      />
                      <span>Mostrar arquivadas</span>
                    </label>
                    {!isArchived ? (
                      <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--primary" onClick={() => setPanelQuestion(null)}>
                        <EcoIcon name="plus" size={16} />
                        Adicionar pergunta
                      </button>
                    ) : null}
                  </div>
                </div>

                {detailQuestions.length === 0 ? (
                  <div className="fForms__pad">
                    <p className="ecoEmpty">Este formulário ainda não tem perguntas.</p>
                  </div>
                ) : (
                  <ol className="fForms__questions">
                    {shownQuestions.map((q) => (
                      <li key={q.id} className={`fForms__question${q.archivedAt ? " is-archived" : ""}`}>
                        <span className="fForms__qOrder">{q.order}</span>
                        <span className="fForms__qText">
                          <span className="fForms__qPrompt">
                            {q.prompt}
                            {q.required ? (
                              <span className="fForms__qReq" title="Obrigatória">
                                {" "}*<span className="ecoVisuallyHidden"> (obrigatória)</span>
                              </span>
                            ) : null}
                          </span>
                          <span className="fForms__qType">
                            {questionTypeText(q)}
                            {q.dimension ? ` · ${q.dimension}` : ""}
                            {q.archivedAt ? " · arquivada" : ""}
                            {choiceWithoutOptions(q) && !q.archivedAt ? (
                              <span className="fForms__qWarn"> · sem opções</span>
                            ) : null}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="ecoBtn ecoBtn--sm"
                          onClick={() => setPanelQuestion(q)}
                          aria-label={`${isArchived || q.archivedAt ? "Ver" : "Editar"} pergunta ${q.order}`}
                        >
                          {isArchived || q.archivedAt ? "Ver" : "Editar"}
                        </button>
                      </li>
                    ))}
                  </ol>
                )}

                {detailQuestions.length > QUESTIONS_PREVIEW ? (
                  <div className="fForms__more">
                    {showAllQuestions ? "" : `Mostrando ${QUESTIONS_PREVIEW} de ${detailQuestions.length} perguntas. `}
                    <button type="button" className="ecoLinkBtn" onClick={() => setShowAllQuestions((v) => !v)}>
                      {showAllQuestions ? "Mostrar menos" : "Ver todas"}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>
      </main>

      <CreateEvaluationFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(created) => {
          setCategory("ALL");
          setSearch("");
          loadForms().then(() => selectForm(created.id));
        }}
      />

      {isInfoOpen && detail ? (
        <FormInfoDialog
          form={detail}
          onClose={() => setIsInfoOpen(false)}
          onSaved={() => {
            setIsInfoOpen(false);
            loadForms();
            loadDetail(detail.id);
          }}
        />
      ) : null}

      {panelQuestion !== undefined && detail ? (
        <QuestionPanel
          key={panelQuestion?.id ?? "nova"}
          formId={detail.id}
          question={panelQuestion}
          nextOrder={nextOrder}
          dimensionSuggestions={dimensionSuggestions}
          readOnly={isArchived}
          onClose={() => setPanelQuestion(undefined)}
          onChanged={refreshAfterQuestionChange}
          onArchived={() => {
            setPanelQuestion(undefined);
            refreshAfterQuestionChange();
          }}
        />
      ) : null}

      <EcoConfirmDialog
        open={confirmArchive && Boolean(detail)}
        title={isArchived ? "Reativar formulário?" : "Arquivar formulário?"}
        message={
          detail ? (
            isArchived ? (
              <>
                <strong>{detail.title}</strong> volta a poder ser usado em novas avaliações.
              </>
            ) : (
              <>
                <strong>{detail.title}</strong> deixa de aparecer para novas avaliações. As avaliações já
                respondidas continuam guardadas, e dá para reativar depois.
              </>
            )
          ) : null
        }
        confirmLabel={isArchived ? "Reativar" : "Arquivar"}
        tone={isArchived ? "default" : "danger"}
        busy={archiving}
        onConfirm={handleToggleArchive}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
};

export default EvaluationFormsAdminPage;
